import React, { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useUserInfo } from '../../Authentication/hooks'
import NavigationComponent from '../navigationComponent'
import { useLoadScript } from '@react-google-maps/api'
import {
  customPostRequest,
  fetchUserSubscription,
  validateEventsAndLocationsData,
  createNavigationHandlers,
  handleAsyncError
} from './utils/categoryUtils'
import '../../../assets/stylesheets/kategories.css'

const libraries = ['places'] // Has to be outside of component

const EventsAndLocationsCategory = () => {
  const [eventsAndLocationsTitle, setEventsAndLocationsTitle] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [location, setLocation] = useState('')
  const [eventsAndLocations, setEventsAndLocations] = useState([])
  const [message, setMessage] = useState('')
  const { userId } = useUserInfo()
  const [categoryCreated, setCategoryCreated] = useState(false)
  const navigate = useNavigate()
  const API_GATEWAY_URL = process.env.REACT_APP_API_GATEWAY_URL
  const [showUpgradeLink, setShowUpgradeLink] = useState(false)

  const { handleHomeClick, handleUploadClick, handleSubscribeClick } =
    createNavigationHandlers(navigate)

  const handleCreate = async () => {
    if (!userId) {
      setMessage('User not authenticated')
      setShowUpgradeLink(false)
      return
    }

    try {
      const subscriptionData = await fetchUserSubscription(
        userId,
        API_GATEWAY_URL
      )
      if (!subscriptionData) {
        setMessage('Failed to fetch subscription data')
        setShowUpgradeLink(true)
        return
      }

      const { categoryCount, categoryLimit } = subscriptionData

      console.log('Subscription Data:', categoryCount + 1, categoryLimit)
      if (categoryCount >= categoryLimit) {
        setMessage('You have reached your category limit.')
        setShowUpgradeLink(true)
        return
      }
    } catch (error) {
      const errorMessage = handleAsyncError(error, 'fetching category count')
      setMessage(`Failed to fetch category count: ${errorMessage}`)
      setShowUpgradeLink(true)
      return
    }

    const categoryData = {
      userId: userId,
      type: 'event',
      subType: 'location'
    }

    if (eventsAndLocationsTitle.trim()) {
      categoryData.name = eventsAndLocationsTitle
    }
    if (fromDate.trim()) {
      categoryData.calendarAttrs = {
        ...categoryData.calendarAttrs,
        fromDate: fromDate + 'T00:00:00Z'
      }
    }
    if (toDate.trim()) {
      categoryData.calendarAttrs = {
        ...categoryData.calendarAttrs,
        toDate: toDate + 'T23:59:59Z'
      }
    }
    if (location.trim()) {
      categoryData.location = { text: location }
    }

    if (!validateEventsAndLocationsData(categoryData, setMessage)) {
      return
    }

    try {
      const apiURL = `${process.env.REACT_APP_API_GATEWAY_URL}/k1/users/${userId}/categories`
      const responseData = await customPostRequest(apiURL, categoryData)

      if (responseData.error) {
        throw new Error(responseData.error)
      }

      setEventsAndLocations(prev => [
        ...prev,
        { eventsAndLocationsTitle, fromDate, toDate, location }
      ])
      setEventsAndLocationsTitle('')
      setFromDate('')
      setToDate('')
      setLocation('')
      setMessage('Category created successfully!')
      setCategoryCreated(true)
    } catch (error) {
      const errorMessage = handleAsyncError(error, 'creating category')
      setMessage(`Failed to create category: ${errorMessage}`)
    }
  }

  //Google Maps API
  const [autocomplete, setAutocomplete] = useState(null)
  const locationInputRef = useRef(null)

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries
  })

  useEffect(() => {
    if (isLoaded && !autocomplete) {
      console.log('Initializing Google Places Autocomplete')
      const googleMapsAutocomplete = new window.google.maps.places.Autocomplete(
        locationInputRef.current,
        {}
      )
      googleMapsAutocomplete.addListener('place_changed', () => {
        const selectedPlace = googleMapsAutocomplete.getPlace()
        const address = selectedPlace.formatted_address || selectedPlace.name
        setLocation(address)
      })
      setAutocomplete(googleMapsAutocomplete)
    }
  }, [isLoaded, autocomplete])

  return (
    <div className="vacation-category">
      <NavigationComponent handleHomeClick={handleHomeClick} />
      <h2 className="category-header">Events & Locations</h2>
      {message && <p className="message">{message}</p>}
      {showUpgradeLink && (
        <p>
          Please{' '}
          <span className="link-like" onClick={handleSubscribeClick}>
            upgrade
          </span>{' '}
          your plan.
        </p>
      )}
      {message === 'Category created successfully!' && (
        <p>
          <Link to="/gallery">Click here</Link> to return to the main gallery.
        </p>
      )}
      <input
        type="text"
        placeholder="Events & Locations Title"
        value={eventsAndLocationsTitle}
        onChange={e => setEventsAndLocationsTitle(e.target.value)}
        className="input-field"
      />
      <input
        type="date"
        value={fromDate}
        onChange={e => setFromDate(e.target.value)}
        className="input-field"
      />
      <input
        type="date"
        value={toDate}
        onChange={e => setToDate(e.target.value)}
        className="input-field"
      />
      <input
        ref={locationInputRef}
        type="text"
        placeholder="Location"
        value={location}
        onChange={e => setLocation(e.target.value)}
        className="input-field"
      />
      <div className="button-group">
        <button onClick={handleCreate} className="create-button">
          Create
        </button>
      </div>
      {categoryCreated && (
        <div className="list_and_upload">
          <ul>
            {eventsAndLocations.map((event, index) => (
              <li key={index} className="added-list">
                {event.eventsAndLocationsTitle} -
                {event.fromDate && `${event.fromDate} to `}
                {event.toDate && event.toDate}
                {event.location && ` at ${event.location}`}
              </li>
            ))}
          </ul>
          <button className="upload-button-list" onClick={handleUploadClick}>
            Upload Photos
          </button>
        </div>
      )}
    </div>
  )
}

export default EventsAndLocationsCategory
